/* eslint-disable max-len */
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { detectHeightVolumeGaps } from './GapAnalysisUtils';

const missingHeightGapPlugin = {
  id: 'missingHeightGapPlugin',
  beforeDatasetsDraw: (chart) => {
    const anomalySegments = chart.options.plugins.missingHeightGapPlugin?.anomalySegments;
    if (!anomalySegments || anomalySegments.length === 0) return;

    const {
      ctx,
      chartArea,
      scales,
    } = chart;
    const xScale = scales.x;

    ctx.save();
    anomalySegments.forEach((segment) => {
      const xStart = xScale.getPixelForValue(segment.startHeight);
      const xEnd = xScale.getPixelForValue(segment.endHeight);
      const left = Math.min(xStart, xEnd);
      const width = Math.max(Math.abs(xEnd - xStart), 3);

      ctx.fillStyle = 'rgba(245, 124, 0, 0.10)';
      ctx.fillRect(left, chartArea.top, width, chartArea.bottom - chartArea.top);
      ctx.strokeStyle = 'rgba(245, 124, 0, 0.85)';
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(left + (width / 2), chartArea.top);
      ctx.lineTo(left + (width / 2), chartArea.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(245, 124, 0, 0.95)';
      ctx.font = '600 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(segment.label, left + (width / 2), chartArea.top + 14);
    });
    ctx.restore();
  },
};

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  missingHeightGapPlugin,
);

export default function HeightVolumeChart({
  heights,
  volumes,
  compartmentId,
  pointPageNumbers,
  boundaryContext,
}) {
  const theme = useTheme();
  const chartAnomalies = detectHeightVolumeGaps(
    heights,
    volumes,
    pointPageNumbers,
    boundaryContext,
  );
  const anomalyColor = theme.palette.warning.main;
  const primaryColor = theme.palette.primary.main;

  // Prepare data for Chart.js
  const chartData = {
    datasets: [
      {
        label: 'Volume (m³)',
        data: heights.map((height, index) => ({
          x: height,
          y: volumes[index],
        })),
        borderColor: primaryColor,
        backgroundColor: `${primaryColor}20`,
        borderWidth: 2,
        pointBackgroundColor: (context) => (
          chartAnomalies.anomalyPoints.has(context.dataIndex) ? anomalyColor : primaryColor
        ),
        pointBorderColor: (context) => (
          chartAnomalies.anomalyPoints.has(context.dataIndex) ? anomalyColor : primaryColor
        ),
        pointRadius: (context) => (
          chartAnomalies.anomalyPoints.has(context.dataIndex) ? 4 : 3
        ),
        pointHoverRadius: 5,
        segment: {
          borderColor: (context) => (
            chartAnomalies.anomalySegments.has(context.p0DataIndex) ? anomalyColor : primaryColor
          ),
          borderWidth: (context) => (
            chartAnomalies.anomalySegments.has(context.p0DataIndex) ? 4 : 2
          ),
          borderDash: (context) => (
            chartAnomalies.anomalySegments.has(context.p0DataIndex) ? [6, 4] : undefined
          ),
        },
        tension: 0.1,
      },
      ...chartAnomalies.boundaryGaps.map((gap) => ({
        label: 'Écart à vérifier',
        data: [
          {
            x: gap.startHeight,
            y: gap.startVolume,
          },
          {
            x: gap.endHeight,
            y: gap.endVolume,
          },
        ],
        borderColor: anomalyColor,
        backgroundColor: `${anomalyColor}20`,
        borderWidth: 3,
        borderDash: [6, 4],
        pointBackgroundColor: anomalyColor,
        pointBorderColor: anomalyColor,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0,
      })),
    ],
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const { x: height, y: volume } = context.raw;
            const matchingGap = chartAnomalies.gaps.find((gap) => (
              gap.endHeight === height && gap.endVolume === volume
            ));
            const gapDetails = matchingGap ? [
              matchingGap.label,
              `Δ hauteur: ${matchingGap.deltaHeight.toFixed(3)} m`,
              matchingGap.deltaVolume !== null
                ? `Δ volume: ${matchingGap.deltaVolume.toFixed(3)} m³`
                : null,
              matchingGap.expectedMissingHeights.length > 0
                ? `Hauteur(s) possible(s): ${matchingGap.expectedMissingHeights.join(', ')} m`
                : null,
            ].filter(Boolean) : [];

            return [
              `Hauteur: ${height.toFixed(3)} m`,
              `Volume: ${volume.toFixed(3)} m³`,
              ...gapDetails,
            ];
          },
        },
      },
      missingHeightGapPlugin: {
        anomalySegments: chartAnomalies.heightGapSegments,
      },
    },
    scales: {
      x: {
        type: 'linear',
        title: {
          display: true,
          text: 'Hauteur (m)',
        },
        ticks: {
          callback: (value) => `${Number(value).toFixed(3)}m`,
        },
        grid: {
          color: theme.palette.divider,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Volume (m³)',
        },
        grid: {
          color: theme.palette.divider,
        },
      },
    },
  };

  // Calculate some statistics
  const minHeight = Math.min(...heights);
  const maxHeight = Math.max(...heights);
  const minVolume = Math.min(...volumes);
  const maxVolume = Math.max(...volumes);
  const heightRange = maxHeight - minHeight;
  const volumeRange = maxVolume - minVolume;
  const dataPointCount = heights.length;
  const averageHeightStep = heightRange > 0 && dataPointCount > 1
    ? heightRange / (dataPointCount - 1)
    : 0;
  const averageVolumeStep = volumeRange > 0 && dataPointCount > 1
    ? volumeRange / (dataPointCount - 1)
    : 0;

  const keyMetrics = [
    {
      label: 'Plage de hauteur',
      value: `${minHeight.toFixed(3)} - ${maxHeight.toFixed(3)} m`,
    },
    {
      label: 'Plage de volume',
      value: `${minVolume.toFixed(3)} - ${maxVolume.toFixed(3)} m³`,
    },
    {
      label: 'Points de données',
      value: dataPointCount,
    },
    {
      label: 'Pas moyen hauteur',
      value: `${averageHeightStep.toFixed(4)} m`,
    },
    {
      label: 'Pas moyen volume',
      value: `${averageVolumeStep.toFixed(3)} m³`,
    },
    {
      label: 'Capacité mesurée',
      value: `${maxVolume.toFixed(3)} m³`,
    },
  ];
  if (heights.length === 0 || volumes.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Aucune donnée disponible pour le graphique
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: {
            xs: 'repeat(1, minmax(0, 1fr))',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
          },
          mb: 2,
        }}
      >
        {keyMetrics.map((metric) => (
          <Box
            key={metric.label}
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 0.75,
              border: (theme.palette.mode === 'light'
                ? '1px solid rgba(0, 0, 0, 0.06)'
                : '1px solid rgba(255, 255, 255, 0.1)'),
              p: 1.5,
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: 'text.secondary', letterSpacing: 0.6 }}
            >
              {metric.label}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {metric.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {chartAnomalies.anomalyCount > 0 && (
        <Box
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 1,
            borderRadius: 0.75,
            border: `1px solid ${anomalyColor}`,
            color: 'warning.dark',
            backgroundColor: `${anomalyColor}14`,
            fontSize: '0.875rem',
          }}
        >
          {chartAnomalies.anomalyCount}
          {' '}
          segment
          {chartAnomalies.anomalyCount > 1 ? 's' : ''}
          {' '}
          suspect
          {chartAnomalies.anomalyCount > 1 ? 's' : ''}
          {' '}
          marqué
          {chartAnomalies.anomalyCount > 1 ? 's' : ''}
          {' '}
          en pointillés.
        </Box>
      )}

      <Box sx={{ height: 300, width: '100%', mb: 2 }}>
        <Line data={chartData} options={chartOptions} />
      </Box>

    </Box>
  );
}
